/**
 * @fileoverview Feriados e datas especiais da empresa: a lista e o diálogo de
 * criar e editar. Numa data cadastrada, vale o que está aqui no lugar do horário
 * da semana, em todas as conexões (ResolveEffectiveScheduleService no backend).
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Dialog, IconButton, Switch, TextField, Tooltip } from "@material-ui/core";
import NumberFormat from "react-number-format";
import { toast } from "react-toastify";
import { CalendarPlus, Pencil, Plus, Trash2, X } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../ConfirmationModal";
import { timeRangeError } from "../SchedulesForm";
import { LoadError, LoadingState } from "../Settings/LoadState";
import { formatLocalDate } from "../Triage/helpers";
import NationalHolidaysDialog from "./NationalHolidaysDialog";
import useExceptionStyles from "./styles";

const EMPTY_FORM = { id: null, date: "", isClosed: true, startTime: "", endTime: "", note: "" };

const t = (key) => i18n.t(`settings.schedules.exceptions.${key}`);

// Mesma regra de horário da semana (SchedulesForm).
const validate = (form) => {
  if (!form.date) return t("errors.date");
  return form.isClosed ? null : timeRangeError(form.startTime, form.endTime);
};

const ScheduleExceptionsManager = () => {
  const classes = useExceptionStyles();
  const [exceptions, setExceptions] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  // Diálogo de criar/editar. O formulário fica montado ao fechar (a animação de
  // saída não pisca vazia); `touched` mostra o erro só depois que a pessoa
  // mexeu (o diálogo novo não abre reclamando da data).
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  // Também depois de criar, editar ou remover: a lista nova vale mesmo que a 1ª
  // carga tenha falhado (sai do erro).
  const fetchExceptions = useCallback(async () => {
    const { data } = await api.get("/schedule-exceptions");
    if (!mountedRef.current) return;
    setExceptions(data);
    setLoadState("ready");
  }, []);

  // 1ª carga e "Tentar de novo": sem a lista, nada de afirmar "nenhuma data".
  const load = useCallback(() => {
    setLoadState("loading");
    fetchExceptions().catch(() => mountedRef.current && setLoadState("error"));
  }, [fetchExceptions]);

  useEffect(() => {
    load();
  }, [load]);

  const openForm = (exception) => {
    setTouched(false);
    setDialogOpen(true);
    setForm(
      exception
        ? {
            id: exception.id,
            date: exception.date,
            isClosed: exception.isClosed,
            startTime: exception.startTime || "",
            endTime: exception.endTime || "",
            note: exception.note || "",
          }
        : EMPTY_FORM
    );
  };

  const change = (changes) => {
    setTouched(true);
    setForm((current) => ({ ...current, ...changes }));
  };

  const closeForm = () => {
    if (!saving) setDialogOpen(false);
  };

  const formError = validate(form);

  const handleSave = async () => {
    if (formError) {
      setTouched(true);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        isClosed: form.isClosed,
        startTime: form.isClosed ? null : form.startTime,
        endTime: form.isClosed ? null : form.endTime,
        note: form.note || null,
      };
      if (form.id) await api.put(`/schedule-exceptions/${form.id}`, payload);
      else await api.post("/schedule-exceptions", payload);
      toast.success(t(form.id ? "toasts.updated" : "toasts.created"));
      setDialogOpen(false);
      fetchExceptions().catch(toastError);
    } catch (err) {
      toastError(err);
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const handleRemove = async () => {
    try {
      await api.delete(`/schedule-exceptions/${removing.id}`);
      toast.success(t("toasts.removed"));
      fetchExceptions().catch(toastError);
    } catch (err) {
      toastError(err);
    }
  };

  let list;
  if (loadState === "loading") list = <LoadingState />;
  else if (loadState === "error") list = <LoadError message={t("loadError")} onRetry={load} />;
  else if (exceptions.length === 0) list = <div className={`${classes.list} ${classes.empty}`}>{t("empty")}</div>;
  else
    list = (
      <div className={classes.list}>
        <div className={`${classes.row} ${classes.head}`}>
          <div>{t("columns.date")}</div>
          <div>{t("columns.note")}</div>
          <div>{t("columns.status")}</div>
          <div />
        </div>
        {exceptions.map((exception) => (
          <div key={exception.id} className={classes.row}>
            <div className={classes.date}>{formatLocalDate(exception.date)}</div>
            <div className={classes.note}>{exception.note || "—"}</div>
            <span className={`${classes.status} ${exception.isClosed ? classes.statusClosed : ""}`}>
              {exception.isClosed ? t("closed") : `${exception.startTime} – ${exception.endTime}`}
            </span>
            <div className={classes.actions}>
              <Tooltip title={t("edit")}>
                <IconButton size="small" className={classes.iconButton} onClick={() => openForm(exception)}>
                  <Pencil size={16} strokeWidth={1.8} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("remove")}>
                <IconButton
                  size="small"
                  className={`${classes.iconButton} ${classes.deleteButton}`}
                  onClick={() => setRemoving(exception)}
                >
                  <Trash2 size={16} strokeWidth={1.8} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        ))}
      </div>
    );

  return (
    <>
      <div className={classes.toolbar}>
        <Button
          variant="outlined"
          className={classes.ghostButton}
          startIcon={<CalendarPlus size={16} />}
          onClick={() => setImportOpen(true)}
          disabled={loadState !== "ready"}
        >
          {t("national.button")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          className={classes.primaryButton}
          startIcon={<Plus size={16} />}
          onClick={() => openForm(null)}
        >
          {t("add")}
        </Button>
      </div>
      {list}

      <Dialog open={dialogOpen} onClose={closeForm} PaperProps={{ className: classes.paper }}>
        <div className={classes.dialogHeader}>
          <div>
            <h2 className={classes.dialogTitle}>{t(form.id ? "dialog.editTitle" : "dialog.newTitle")}</h2>
            <p className={classes.dialogDescription}>{t("dialog.description")}</p>
          </div>
          <IconButton
            size="small"
            className={classes.closeButton}
            onClick={closeForm}
            disabled={saving}
            aria-label={t("dialog.cancel")}
          >
            <X size={18} strokeWidth={1.8} />
          </IconButton>
        </div>

        <div className={classes.dialogBody}>
          <div>
            <label className={classes.label} htmlFor="schedule-exception-date">
              {t("dialog.date")}
            </label>
            <TextField
              id="schedule-exception-date"
              type="date"
              variant="outlined"
              size="small"
              className={classes.field}
              value={form.date}
              onChange={(event) => change({ date: event.target.value })}
            />
          </div>

          <div className={classes.switchRow}>
            <span>
              <span className={classes.switchTitle}>{t("dialog.closedAllDay")}</span>
              <span className={classes.switchHint}>{t(form.isClosed ? "dialog.closedHint" : "dialog.reducedHint")}</span>
            </span>
            <Switch
              color="primary"
              checked={form.isClosed}
              onChange={(event) => change({ isClosed: event.target.checked, startTime: "", endTime: "" })}
              inputProps={{ "aria-label": t("dialog.closedAllDay") }}
            />
          </div>

          {!form.isClosed && (
            <div>
              <span className={classes.label}>{t("dialog.hours")}</span>
              <div className={classes.timeRow}>
                <NumberFormat
                  customInput={TextField}
                  variant="outlined"
                  size="small"
                  format="##:##"
                  placeholder="09:00"
                  className={classes.field}
                  value={form.startTime}
                  onValueChange={({ formattedValue }) => change({ startTime: formattedValue })}
                  inputProps={{ "aria-label": i18n.t("settings.schedules.form.initialHour") }}
                />
                <span className={classes.timeDash}>–</span>
                <NumberFormat
                  customInput={TextField}
                  variant="outlined"
                  size="small"
                  format="##:##"
                  placeholder="14:00"
                  className={classes.field}
                  value={form.endTime}
                  onValueChange={({ formattedValue }) => change({ endTime: formattedValue })}
                  inputProps={{ "aria-label": i18n.t("settings.schedules.form.finalHour") }}
                />
              </div>
            </div>
          )}

          <div>
            <label className={classes.label} htmlFor="schedule-exception-note">
              {t("dialog.note")}
            </label>
            <TextField
              id="schedule-exception-note"
              variant="outlined"
              size="small"
              className={classes.field}
              value={form.note}
              onChange={(event) => change({ note: event.target.value })}
              placeholder={t("dialog.notePlaceholder")}
            />
          </div>

          {touched && formError && (
            <p className={classes.error} role="alert">
              {formError}
            </p>
          )}
        </div>

        <div className={classes.dialogActions}>
          <Button variant="outlined" className={classes.ghostButton} onClick={closeForm} disabled={saving}>
            {t("dialog.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.primaryButton}
            onClick={handleSave}
            disabled={saving || (touched && !!formError)}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {t(form.id ? "dialog.save" : "dialog.create")}
          </Button>
        </div>
      </Dialog>

      <NationalHolidaysDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        exceptions={exceptions}
        onImported={() => fetchExceptions().catch(toastError)}
      />

      <ConfirmationModal
        title={t("deleteTitle")}
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={handleRemove}
        confirmLabel={t("deleteConfirm")}
      >
        {t("deleteBody")}
      </ConfirmationModal>
    </>
  );
};

export default ScheduleExceptionsManager;
